/* ==========================================================================
   SPACE STYLING CONCEPTS（空间搭配页）行为
   稿：vvrvlDhEzVWP1shCOrDZbg   规格 figma-qa/specs/space-styling.md
   方案 figma-qa/tasks/space-styling-20260907.md   审计 figma-qa/reports/theme-audit-space-styling.md

   负责：
     1. 场景轮播：prev / next / 键盘 ←→ / 触摸滑动 / 可选自动播放（循环）
     2. 热点点击 → 打开右侧 PRODUCT DETAIL 抽屉（抽屉本身由 assets/amada-quickview.js 接管），
        同时给对应商品卡 + 该热点加 .is-active
     3. 切场景 → 下方卡片区整组切换（Liquid 已预渲染全部场景的卡片，只切 hidden，不走 AJAX）
     4. 最小加购：POST /cart/add.js（8s 超时）→ window.AmadaCartDrawer.open({justAdded:true})，
        成功后按钮加 .is-added（白底黑框）1500ms（2026-09-08 返修 #2）
     5. 热点定位：坐标是**相对原图**的百分比，layoutHotspots() 按 object-fit:cover 的实际绘制矩形
        换算成容器内 px（2026-09-08 返修 #5）——PC/手机共用一套值，不再靠 x_mobile 手工补
     6. 露出图（左右各一块）点击 = 切场景，hover 出箭头（2026-09-08 返修 #1）

   🚨 三条必须记住的坑：
   · **热点的高亮为什么用 pointerdown 而不是 click**：assets/amada-quickview.js 在
     `document` 的**捕获阶段**监听 click，命中 [data-amada-quickview-open] 就
     `stopImmediatePropagation()`（audit §1）。捕获阶段在 document 上掐断后，事件根本到不了
     本页根元素，任何写在 root 上的 click 监听都不会触发。pointerdown 早于 click 且不被拦截。
   · **不引入 assets/amada-search.js**：它的加购处理器要求 `.closest('[data-amada-search]')`
     祖先（amada-search.js:44-46），本页必然「点了没反应」，且会带入搜索层的 Escape/返回逻辑。
     所以这里自带一份最小加购，调用序列与 amada-search.js:10-40 一致。
   · **铁律 10：带宽度判断的逻辑禁止「加载时宽度不符就 return」**。本文件里没有任何
     「桌面才初始化」的分支：轨道位移一律按实测几何算（scene.offsetLeft / stage.clientWidth），
     两端共用，resize 时重算 —— 桌面加载 → 缩到 375 → 再放大也不会残留错位。
   ========================================================================== */
(function () {
  'use strict';

  // 幂等守卫：脚本被重复引入（主题编辑器、多次 render）时只装一次全局监听
  if (window.__amadaSpaceBooted) return;
  window.__amadaSpaceBooted = true;

  var ADD_TO_CART_TIMEOUT = 8000; // 铁律 31：任何 fetch 都必须带超时，否则失败表现成无限转圈
  var SWIPE_THRESHOLD = 40; // px，横向位移超过它才算一次滑动
  var ADDED_FEEDBACK_MS = 1500; // 加购成功后「白底黑框」保持时长（裁决 §13-2）
  var MOBILE_MAX = 959; // 全站断点：≤959 = 移动端

  /* object-position 的一个分量 → 0..1 的对齐比例。
     computed 值一般是 "50% 100%" 这种百分比；关键字（center/top/bottom/left/right）与 px 都兜住。
     px 的语义是「距容器左/上边缘的偏移」，与百分比不同，单独返回让调用方区分。 */
  function parsePositionPart(raw, isVertical) {
    var v = String(raw || '').trim().toLowerCase();
    if (v === 'center') return { ratio: 0.5 };
    if (v === 'left' || v === 'top') return { ratio: 0 };
    if (v === 'right' || v === 'bottom') return { ratio: 1 };
    if (v.slice(-1) === '%') {
      var pct = parseFloat(v);
      return { ratio: isNaN(pct) ? 0.5 : pct / 100 };
    }
    if (v.slice(-2) === 'px') {
      var px = parseFloat(v);
      return { px: isNaN(px) ? 0 : px };
    }
    var num = parseFloat(v);
    return { ratio: isNaN(num) ? 0.5 : num / 100 };
  }

  /* 🚨 返修 #5（裁决 §13-5）：热点坐标语义 = **相对未裁切原图**的百分比。
     图是 object-fit:cover，两端（PC 1920/710 · Mobile 375/274）裁掉的区域完全不同，
     所以同一套「相对容器」的百分比在 PC 落在沙发上、在手机就会跑到墙上（运营截图 16.png）。
     这里按 cover 的规则算出「原图在容器里的实际绘制矩形」，再把源图百分比映射成容器内 px：
        scale  = max(容器宽/原图宽, 容器高/原图高)
        绘制宽 = 原图宽 × scale（高同理）
        偏移   = (容器尺寸 − 绘制尺寸) × object-position 比例   ← 负数，就是被裁掉的部分
     图片未 load（naturalWidth=0）时直接返回，热点保持 visibility:hidden（CSS 兜底），不会闪错位。 */
  function layoutHotspots(scene) {
    if (!scene) return;
    var img = scene.querySelector('.amada-space__scene-img');
    var dots = scene.querySelectorAll('.amada-space__hotspot');
    if (!img || !dots.length) return;

    var nw = img.naturalWidth;
    var nh = img.naturalHeight;
    var cw = img.clientWidth;
    var ch = img.clientHeight;
    if (!nw || !nh || !cw || !ch) return;

    var scale = Math.max(cw / nw, ch / nh);
    var dw = nw * scale;
    var dh = nh * scale;

    var pos = (window.getComputedStyle(img).objectPosition || '50% 50%').split(/\s+/);
    var px = parsePositionPart(pos[0], false);
    var py = parsePositionPart(pos.length > 1 ? pos[1] : pos[0], true);
    var left = typeof px.px === 'number' ? px.px : (cw - dw) * px.ratio;
    var top = typeof py.px === 'number' ? py.px : (ch - dh) * py.ratio;

    var isMobile = window.matchMedia && window.matchMedia('(max-width: ' + MOBILE_MAX + 'px)').matches;

    Array.prototype.forEach.call(dots, function (dot) {
      var xAttr = isMobile ? dot.getAttribute('data-x-mobile') : null;
      var yAttr = isMobile ? dot.getAttribute('data-y-mobile') : null;
      var x = parseFloat(xAttr !== null && xAttr !== '' ? xAttr : dot.getAttribute('data-x'));
      var y = parseFloat(yAttr !== null && yAttr !== '' ? yAttr : dot.getAttribute('data-y'));
      if (isNaN(x) || isNaN(y)) return; // 没给坐标就交回 CSS 兜底
      dot.style.left = Math.round((left + (dw * x) / 100) * 100) / 100 + 'px';
      dot.style.top = Math.round((top + (dh * y) / 100) * 100) / 100 + 'px';
    });

    scene.classList.add('is-hotspots-ready');
  }

  function toEl(node) {
    return node && node.nodeType === 1 ? node : null;
  }

  function closestFrom(evt, selector) {
    var el = toEl(evt.target);
    return el ? el.closest(selector) : null;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ── 最小加购（调用序列照抄 assets/amada-search.js:10-40，去掉搜索层专属逻辑）──────── */
  function addToCart(btn) {
    var variantId = Number(btn.getAttribute('data-variant-id'));
    if (!variantId) {
      console.error('[amada-space] 加购缺少 data-variant-id，按钮：', btn);
      return;
    }

    var defaultLabel = btn.getAttribute('data-label-default') || btn.textContent;
    var errorLabel = btn.getAttribute('data-label-error') || 'Try again';
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller
      ? setTimeout(function () {
          controller.abort();
        }, ADD_TO_CART_TIMEOUT)
      : null;

    /* 请求中：用 is-busy 锁交互，**不用 disabled** —— disabled 会命中售罄灰（#B8B8B8），
       表现成「黑 → 灰 → 白」三段变色（运营 2026-09-08 反馈）。
       同时立刻进入「白底黑框」态（裁决 §13-2 的「点击」态），请求期间保持，成功后再延 1500ms。 */
    btn.classList.add('is-busy');
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('is-added');
    if (btn.__amadaAddedTimer) {
      clearTimeout(btn.__amadaAddedTimer);
      btn.__amadaAddedTimer = null;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        if (!res.ok) throw new Error('/cart/add.js ' + res.status);
        return res.json();
      })
      .then(function () {
        if (timer) clearTimeout(timer);
        btn.classList.remove('is-busy');
        btn.removeAttribute('aria-busy');
        /* 返修 #2（裁决 §13-2）：「白底 + 1px 黑框 + 黑字」态从点击起持续到成功后 1500ms，
           然后移除类自动恢复黑底白字。样式在 amada-space-styling.css 的
           `.amada-space__card-add.is-added` 里，与 :active 同一条规则。 */
        btn.__amadaAddedTimer = setTimeout(function () {
          btn.classList.remove('is-added');
          btn.__amadaAddedTimer = null;
        }, ADDED_FEEDBACK_MS);
        // 全站规则：加购成功弹出购物袋抽屉。window.AmadaCartDrawer 由 sections/quick-cart.liquid
        // 导出，经 overlay-group 全站渲染，本页无需任何额外依赖（audit §2）。
        if (window.AmadaCartDrawer && typeof window.AmadaCartDrawer.open === 'function') {
          window.AmadaCartDrawer.open({ justAdded: true });
        } else {
          window.location.href = '/cart';
        }
      })
      .catch(function (err) {
        if (timer) clearTimeout(timer);
        btn.classList.remove('is-busy');
        btn.removeAttribute('aria-busy');
        btn.classList.remove('is-added');
        // 铁律 31：先分清「代码 bug」和「网络不可达」——把真实原因打出来，别只留个转圈
        console.error('[amada-space] 加购失败：', err);
        btn.classList.add('is-error');
        btn.textContent = errorLabel;
        setTimeout(function () {
          btn.classList.remove('is-error');
          btn.textContent = defaultLabel;
        }, 2000);
      });
  }

  /* ── 单个 section 实例 ────────────────────────────────────────────────── */
  function initRoot(root) {
    if (root.__amadaSpaceInited) return;
    root.__amadaSpaceInited = true;

    var stage = root.querySelector('[data-space-stage]');
    var track = root.querySelector('[data-space-track]');
    var scenes = Array.prototype.slice.call(root.querySelectorAll('.amada-space__scene'));
    var groups = Array.prototype.slice.call(root.querySelectorAll('.amada-space__cards-group'));
    var prevBtn = root.querySelector('[data-space-prev]');
    var nextBtn = root.querySelector('[data-space-next]');
    var index = 0;
    var autoplayTimer = null;

    /* 轨道位移：把当前场景的中心对到舞台中心。
       offsetLeft / offsetWidth 是布局值（会跟随下面 applyOrder 设的 flex order），
       不受已应用的 transform 影响，两端共用一套算法。 */
    function layout() {
      if (!track || !stage || !scenes.length) return;
      var current = scenes[index];
      if (!current) return;
      var offset = current.offsetLeft + current.offsetWidth / 2 - stage.clientWidth / 2;
      track.style.transform = 'translateX(' + -Math.round(offset * 100) / 100 + 'px)';
    }

    /* 🚨 稿里主图两侧永远各露出一张相邻场景（上一张的右缘 / 下一张的左缘）。
       DOM 顺序固定时，第 1 张的左边没有东西 → 左侧露出位是空的（实测第一版就是这样）。
       解法：用 flex `order` 把轨道**永远**排成 [上一张, 当前, 下一张, …]，
       当前场景恒在第 2 个槽位，两侧自然有图。order 会被 offsetLeft 如实反映，
       所以 layout() 不用改。 */
    /* 轨道位移 + 全部场景的热点换算一起做：热点是相对源图定位的，
       容器尺寸一变（resize / 场景高度 80%↔100% 的过渡 / 图片 load）就必须重算。 */
    function layoutAll() {
      layout();
      scenes.forEach(layoutHotspots);
    }

    function applyOrder() {
      var n = scenes.length;
      scenes.forEach(function (scene, i) {
        scene.style.order = String((((i - index + 1) % n) + n) % n);
      });
    }

    function applyClasses() {
      scenes.forEach(function (scene, i) {
        scene.classList.toggle('is-current', i === index);
        // 露出图上的点不可聚焦（避免 Tab 跑到看不见的按钮上）
        Array.prototype.forEach.call(scene.querySelectorAll('.amada-space__hotspot'), function (h) {
          if (i === index) {
            h.removeAttribute('tabindex');
          } else {
            h.setAttribute('tabindex', '-1');
          }
        });
      });
      groups.forEach(function (group) {
        group.hidden = Number(group.getAttribute('data-scene-index')) !== index;
      });
    }

    function clearActive() {
      Array.prototype.forEach.call(root.querySelectorAll('.is-active'), function (el) {
        el.classList.remove('is-active');
      });
    }

    function goTo(i) {
      var n = scenes.length;
      if (!n || !track || !stage) return;
      var next = (((i % n) + n) % n); // 循环
      if (next === index) return;

      /* 重排会瞬间改变各场景的槽位 → 直接切会「跳一下」。
         做法：先记下目标场景**当前的视觉位置**，重排后用无过渡的补偿把它钉回原处，
         强制回流后再开过渡滑到中心 —— 观感上就是一次平滑滑动。 */
      var target = scenes[next];
      var stageRect = stage.getBoundingClientRect();
      var beforeRect = target.getBoundingClientRect();
      var beforeCenter = beforeRect.left + beforeRect.width / 2 - stageRect.left;

      index = next;
      clearActive(); // 换了场景，上一场景的选中态不再有意义

      track.style.transition = 'none';
      applyOrder();
      applyClasses();
      track.style.transform =
        'translateX(' + Math.round((beforeCenter - (target.offsetLeft + target.offsetWidth / 2)) * 100) / 100 + 'px)';
      void track.offsetWidth; // 强制回流，否则上下两次 transform 会被合并成一次跳变
      track.style.transition = '';
      layoutAll();
    }

    /* 热点 / 卡片被点 → 高亮对应的卡片与热点（抽屉由 amada-quickview.js 打开） */
    function setActive(handle) {
      if (!handle) return;
      Array.prototype.forEach.call(root.querySelectorAll('.amada-space__card'), function (card) {
        card.classList.toggle('is-active', card.getAttribute('data-product-handle') === handle);
      });
      Array.prototype.forEach.call(root.querySelectorAll('.amada-space__hotspot'), function (dot) {
        dot.classList.toggle('is-active', dot.getAttribute('data-product-handle') === handle);
      });
    }

    /* ── 自动播放（可选，默认关）───────────────────────────────────────── */
    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    function startAutoplay() {
      stopAutoplay();
      if (root.getAttribute('data-autoplay') !== 'true') return;
      if (scenes.length < 2 || prefersReducedMotion()) return;
      var interval = parseInt(root.getAttribute('data-autoplay-interval'), 10) || 6000;
      autoplayTimer = setInterval(function () {
        if (document.hidden) return;
        goTo(index + 1);
      }, interval);
    }

    /* 鼠标点击（e.detail ≥ 1）后主动 blur：焦点留在切场景按钮上会随轨道重排跟着元素跑
       （运营 2026-09-08 实测「点完跑到旁边的图上」）。键盘触发（e.detail === 0）
       保留焦点，可访问性不受影响。
       🚨 2026-09-14 起箭头**无条件常显**，blur 不再承担显隐职责（CSS 里已无 :focus-visible
          显隐规则），但仍必须保留：焦点粘在按钮上会留下焦点环、并让 :active 态颜色残留，
          正是运营反馈「箭头时有时无」的另一半来源。 */
    function blurIfMouse(e) {
      if (e && e.detail > 0 && e.currentTarget && typeof e.currentTarget.blur === 'function') {
        e.currentTarget.blur();
      }
    }

    /* ── 绑定 ─────────────────────────────────────────────────────────── */
    /* 主图内缘的 prev / next 箭头（2026-09-14 用户拍板恢复并常显）。 */
    if (prevBtn) {
      prevBtn.addEventListener('click', function (e) {
        goTo(index - 1);
        startAutoplay(); // 手动操作后重新计时
        blurIfMouse(e); // 鼠标点击后放掉焦点，避免残留焦点环 / :active 色
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function (e) {
        goTo(index + 1);
        startAutoplay();
        blurIfMouse(e);
      });
    }

    /* 露出图整块可点 = 切到该场景（返修 #1，裁决 §13-1）。
       🚨 用 [data-space-peek] 而不是复用 [data-space-prev/next]：上面的 querySelector
          只取第一个，复用会让主图箭头失灵。
       ⚠️ 露出图上的圆形箭头（.amada-space__peek-arrow）维持 9-10 的删除，
          这里只有透明热区，没有图形。 */
    Array.prototype.forEach.call(root.querySelectorAll('[data-space-peek]'), function (peek) {
      peek.addEventListener('click', function (e) {
        goTo(peek.getAttribute('data-space-peek') === 'prev' ? index - 1 : index + 1);
        startAutoplay();
        blurIfMouse(e);
      });
    });

    if (stage) {
      // 键盘：←/→（事件从舞台内的按钮冒泡上来）
      stage.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          goTo(index - 1);
          startAutoplay();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          goTo(index + 1);
          startAutoplay();
        }
      });

      // 触摸滑动：只认「横向位移大于纵向」的手势，避免把纵向滚动吃掉
      var touchX = 0;
      var touchY = 0;
      var touching = false;
      stage.addEventListener(
        'touchstart',
        function (e) {
          if (!e.touches || e.touches.length !== 1) return;
          touching = true;
          touchX = e.touches[0].clientX;
          touchY = e.touches[0].clientY;
          stopAutoplay();
        },
        { passive: true }
      );
      stage.addEventListener(
        'touchend',
        function (e) {
          if (!touching) return;
          touching = false;
          var t = (e.changedTouches && e.changedTouches[0]) || null;
          if (!t) return;
          var dx = t.clientX - touchX;
          var dy = t.clientY - touchY;
          if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
            goTo(dx < 0 ? index + 1 : index - 1);
          }
          startAutoplay();
        },
        { passive: true }
      );

      stage.addEventListener('mouseenter', stopAutoplay);
      stage.addEventListener('mouseleave', startAutoplay);
      stage.addEventListener('focusin', stopAutoplay);
      stage.addEventListener('focusout', startAutoplay);
    }

    /* 高亮：必须用 pointerdown（click 会被 amada-quickview.js 在 document 捕获阶段掐断），
       键盘激活补一条 keyup（Enter / 空格）。两条都不阻止冒泡，抽屉照常打开。 */
    root.addEventListener('pointerdown', function (e) {
      var trigger = closestFrom(e, '[data-amada-quickview-open]');
      if (!trigger) return;
      setActive(trigger.getAttribute('data-product-handle'));
    });
    root.addEventListener('keyup', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      var trigger = closestFrom(e, '[data-amada-quickview-open]');
      if (!trigger) return;
      setActive(trigger.getAttribute('data-product-handle'));
    });

    // 加购（心形/快速查看各管各的，这里只认 [data-amada-card-add]，不阻止冒泡）
    root.addEventListener('click', function (e) {
      var btn = closestFrom(e, '[data-amada-card-add]');
      if (!btn || btn.disabled || btn.classList.contains('is-busy')) return;
      addToCart(btn);
    });

    /* 尺寸变化重算轨道位移 + 热点（含桌面→移动→桌面的往返，铁律 10/23）。
       用 rAF 节流：resize 期间每帧最多算一次，避免 80ms 防抖导致热点「慢半拍」地追着容器跑。 */
    var resizeRaf = null;
    function onResize() {
      if (resizeRaf) return;
      resizeRaf = (window.requestAnimationFrame || window.setTimeout)(function () {
        resizeRaf = null;
        layoutAll();
      }, 16);
    }
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    /* 大图是懒加载/响应式的：naturalWidth 只有 load 之后才有值，而热点换算完全依赖它 ——
       load 前热点保持 visibility:hidden（CSS），load 后立刻算一次并显示。
       已经 complete 的（缓存命中）也要补算，否则永远等不到 load 事件。
       （srcset 切档换了更大的图不用重算：naturalWidth 变了但**长宽比不变**，
       cover 算出来的绘制矩形逐像素相同。） */
    Array.prototype.forEach.call(root.querySelectorAll('.amada-space__scene-img'), function (img) {
      var scene = img.closest('.amada-space__scene');
      if (img.complete && img.naturalWidth) {
        layoutHotspots(scene);
      } else {
        img.addEventListener(
          'load',
          function () {
            layout();
            layoutHotspots(scene);
          },
          { once: true }
        );
      }
    });

    /* 场景盒自身的尺寸变化（非当前场景 80% 高 ↔ 当前 100% 高的 400ms 过渡、
       字体/图片回流）不会触发 window.resize —— 用 ResizeObserver 逐帧跟。 */
    if (typeof ResizeObserver === 'function') {
      var ro = new ResizeObserver(function (entries) {
        entries.forEach(function (entry) {
          layoutHotspots(entry.target);
        });
      });
      scenes.forEach(function (scene) {
        ro.observe(scene);
      });
    }

    // 首帧不要动画（否则一进页面轨道会从 0 滑到中心）
    track.style.transition = 'none';
    applyOrder();
    applyClasses();
    layoutAll();
    void track.offsetWidth;
    track.style.transition = '';

    startAutoplay();
  }

  function initAll(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(host.querySelectorAll('.amada-space[data-section-id]'), initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  // 主题编辑器里增删/重排 section 后重新初始化
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target || document);
  });
})();
